import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import client from '../api/client';

const PersonContactsContext = createContext(null);

function normalizeUserId(userId) {
  const id = Number(userId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function PersonContactsProvider({ userIds, children }) {
  const [contacts, setContacts] = useState({});
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const fetchedRef = useRef(new Set());
  const inFlightRef = useRef(null);

  useEffect(() => {
    const ids = [...new Set((userIds || []).map(normalizeUserId).filter(Boolean))];
    const missing = ids.filter((id) => !fetchedRef.current.has(id));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    const key = missing.slice().sort((a, b) => a - b).join(',');

    if (inFlightRef.current === key) return undefined;
    inFlightRef.current = key;

    (async () => {
      try {
        const res = await client.post('/api/cache/users/lookup', { userIds: missing });
        if (cancelled) return;
        const users = res.data?.users || {};
        const merged = { ...users };
        missing.forEach((id) => {
          const k = String(id);
          if (!merged[k]) merged[k] = { userId: id };
        });
        missing.forEach((id) => fetchedRef.current.add(id));
        setContacts((prev) => ({ ...prev, ...merged }));
        setResolvedIds((prev) => {
          const next = new Set(prev);
          missing.forEach((id) => next.add(id));
          return next;
        });
      } catch (err) {
        console.error('Person contact lookup failed:', err);
        missing.forEach((id) => fetchedRef.current.add(id));
        setResolvedIds((prev) => {
          const next = new Set(prev);
          missing.forEach((id) => next.add(id));
          return next;
        });
      } finally {
        if (!cancelled && inFlightRef.current === key) {
          inFlightRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userIds]);

  const getContact = useCallback(
    (userId) => {
      const id = normalizeUserId(userId);
      if (!id) return null;
      return contacts[String(id)] ?? contacts[id] ?? null;
    },
    [contacts],
  );

  const isResolved = useCallback(
    (userId) => {
      const id = normalizeUserId(userId);
      return id ? resolvedIds.has(id) : false;
    },
    [resolvedIds],
  );

  return (
    <PersonContactsContext.Provider value={{ getContact, isResolved }}>
      {children}
    </PersonContactsContext.Provider>
  );
}

export function usePersonContact(userId) {
  const ctx = useContext(PersonContactsContext);
  const id = normalizeUserId(userId);
  if (!ctx || !id) return { contact: null, resolved: false };
  return {
    contact: ctx.getContact(id),
    resolved: ctx.isResolved(id),
  };
}
